import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import PatientRecordFilterOption from '../../../../common/models/PatientRecordFilterOption';
import { Random } from '@zedoc/random';
import { apiZedocSearchPatientRecordsFilterOptions } from '../../../../common/api/zedoc';
import createSelectors from '../../../../common/selectors/createSelectors';
import defaultFilters from './defaultFilters';
import FiltersToolbar from './FiltersToolbar';
import { FILTER_CATEGORY__VARIABLE } from '../../../../components/Filters';
import useProjectFilters from '../../../../utils/useProjectFilters';
import Recipient from '../../../../common/models/Recipient';
import Participation from '../../../../common/models/Participation';
import {
  FILTER_CONDITION__FULL_TEXT,
  FILTER_TYPE__ALL_OF,
  VARIABLE_ID__NAME,
  VARIABLE_ID__NAME_FIRST,
  VARIABLE_ID__NAME_LAST,
} from '../../../../common/constants';

const PatientRecordFilterOptionSelect = createSelectors(
  PatientRecordFilterOption,
  {},
);

const ALLOWED_SCOPES = [Recipient.scopeName, Participation.scopeName];

/**
 * The filter presets of this screen. Shared with ProjectParticipations, which
 * has to know whether any quick-search box will be written before it
 * subscribes - both must look at exactly the same list.
 *
 * @param {string} projectId
 */
export const useParticipationsPresets = (projectId) =>
  useProjectFilters({
    projectId,
    defaultFilters,
    defaultCategory: FILTER_CATEGORY__VARIABLE,
    allowedScopes: ALLOWED_SCOPES,
  });

/**
 * True for the presets that become quick-search boxes.
 *
 * @param {object} preset
 * @returns {boolean}
 */
export const isQuickSearchPreset = (preset) =>
  preset.settings.id === VARIABLE_ID__NAME ||
  preset.settings.id === VARIABLE_ID__NAME_FIRST ||
  preset.settings.id === VARIABLE_ID__NAME_LAST;

const ConnectedFilters = ({ projectId }) => {
  const presets = useParticipationsPresets(projectId);
  const getOptionsSubscription = useCallback(
    ({
      searchText,
      type,
      condition,
      state,
      settings,
      pageIndex,
      resultsPerPage,
    }) => {
      return apiZedocSearchPatientRecordsFilterOptions.withParams({
        projectId,
        controlId: '$meta.id',
        searchText,
        filter: {
          type,
          condition,
          state,
          settings,
        },
        pageIndex,
        resultsPerPage,
        sorterFilter: {
          label: 1,
        },
      });
    },
    [projectId],
  );

  const simpleFilters = useMemo(
    () =>
      presets
        .map((preset) => {
          if (isQuickSearchPreset(preset)) {
            return {
              id: Random.id(),
              type: FILTER_TYPE__ALL_OF,
              settings: {
                filters: [
                  {
                    ...preset,
                    id: Random.id(),
                    condition: FILTER_CONDITION__FULL_TEXT,
                    state: { text: '' },
                  },
                ],
              },
              meta: {
                nested: true,
              },
            };
          }

          return null;
        })
        .filter(Boolean),
    [presets],
  );

  return (
    <FiltersToolbar
      presets={presets}
      simpleFilters={simpleFilters}
      optionsSelector={PatientRecordFilterOptionSelect}
      optionsSubscription={getOptionsSubscription}
    />
  );
};

ConnectedFilters.propTypes = {
  projectId: PropTypes.string.isRequired,
};

export default ConnectedFilters;
